import { Dispatch, SetStateAction } from "react";
import { useSnackbar } from "notistack";
import { ArrowDownAZ, Funnel, SearchIcon, XIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";

export interface IFilters {
  searchText: string;
  sortBy: {
    label: string;
    value: EnumSortBy;
  };
  sortOrder: {
    label: string;
    value: EnumSortOrder;
  } | null;
}

export enum EnumSortOrder {
  HIGHEST = "highest",
  LOWEST = "lowest",
}

export enum EnumSortBy {
  MOST_LATEST_ACTIVITY = "mostLatestActivity",
  APPLIED_JOBS = "appliedJobs",
  LIVE = "live",
  EXPIRED = "expired",
}

export const sortByFilters = [
  {
    label: "Most Latest Activity",
    value: EnumSortBy.MOST_LATEST_ACTIVITY,
  },
  {
    label: "Applied Jobs",
    value: EnumSortBy.APPLIED_JOBS,
  },
  {
    label: "Live",
    value: EnumSortBy.LIVE,
  },
  {
    label: "Expired",
    value: EnumSortBy.EXPIRED,
  },
];

export const sortOrderFilters = [
  {
    label: "Highest",
    value: EnumSortOrder.HIGHEST,
  },
  {
    label: "Lowest",
    value: EnumSortOrder.LOWEST,
  },
];

const FilterSection = ({
  filters,
  setFilters,
}: {
  filters: IFilters;
  setFilters: Dispatch<SetStateAction<IFilters>>;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isClearSortByEnabled =
    sortByFilters[0]?.value !== filters?.sortBy?.value;
  const isClearSortOrderEnabled = Boolean(filters?.sortOrder);
  const isSortOrderDisabled =
    filters?.sortBy?.value === EnumSortBy.MOST_LATEST_ACTIVITY;

  return (
    <div className="flex items-center justify-between border-b py-3.5">
      {/* Search container */}
      <div className="flex items-center border rounded-md text-xs font-medium px-3 bg-gray-50">
        <input
          className="h-9 flex-1 bg-transparent w-64 outline-none"
          placeholder="Search..."
          value={filters?.searchText}
          onChange={(e) =>
            setFilters((prev) => {
              return { ...prev, searchText: e?.target?.value };
            })
          }
        />
        <SearchIcon color="gray" size={16} />
      </div>
      {/* Sort By */}
      <DropdownMenu>
        <div className="relative mr-2.5">
          <DropdownMenuTrigger className="flex items-center text-charcoal font-semibold text-xs border rounded-md h-9 gap-1 px-3 outline-none w-72">
            <Funnel size={15} />
            Sort By :{" "}
            <span className="text-light-black">{filters?.sortBy?.label}</span>
          </DropdownMenuTrigger>
          {isClearSortByEnabled ? (
            <button
              onClick={() =>
                setFilters((prev) => {
                  return { ...prev, sortBy: sortByFilters[0] };
                })
              }
              className="bg-red-500 rounded-full p-px absolute -top-2 -right-2 hover:scale-125"
            >
              <XIcon color="white" size={13} strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
        <DropdownMenuContent className="bg-white" align="start">
          <div className="flex flex-col gap-2 px-2 py-3">
            <span className="text-xs mb-1">Sort By</span>
            {sortByFilters?.map((filter) => {
              const isSelected = filter?.value === filters?.sortBy?.value;
              const isDisabled =
                filter?.value === EnumSortBy.LIVE ||
                filter?.value === EnumSortBy.EXPIRED;
              return (
                <DropdownMenuItem
                  key={filter?.value}
                  className={`flex gap-3 text-xs outline-none font-medium ${
                    isDisabled
                      ? "cursor-not-allowed opacity-50"
                      : "hover:cursor-pointer hover:bg-gray-50"
                  }`}
                  onSelect={() =>
                    isDisabled
                      ? enqueueSnackbar("Filter is not available.", {
                          variant: "warning",
                        })
                      : setFilters((prev) => {
                          return { ...prev, sortBy: filter };
                        })
                  }
                >
                  <div className="flex w-4 h-4 rounded-full border border-primary">
                    {isSelected ? (
                      <div className="w-2.5 h-2.5 bg-primary rounded-full m-auto" />
                    ) : null}
                  </div>
                  {filter?.label}
                </DropdownMenuItem>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Sort Order */}
      <DropdownMenu>
        <div className="relative mr-2.5">
        <DropdownMenuTrigger
            disabled={isSortOrderDisabled}
            className={`flex items-center text-charcoal font-semibold text-xs border rounded-md h-9 gap-1 px-3 outline-none w-56 ${
              isSortOrderDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <ArrowDownAZ size={15} />
            Sort Order :{" "}
            <span className="text-light-black">
              {filters?.sortOrder?.label || "Select"}
            </span>
          </DropdownMenuTrigger>
          {!isSortOrderDisabled && isClearSortOrderEnabled ? (
            <button
              onClick={() =>
                setFilters((prev) => {
                  return { ...prev, sortOrder: null };
                })
              }
              className="bg-red-500 rounded-full p-px absolute -top-2 -right-2 hover:scale-125"
            >
              <XIcon color="white" size={13} strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
        <DropdownMenuContent className="bg-white" align="start">
          <div className="flex flex-col gap-2 px-2 pt-3">
            <span className="text-xs mb-1">Filter By</span>
            {sortOrderFilters?.map((filter) => {
              const isSelected = filter?.value === filters?.sortOrder?.value;
              return (
                <DropdownMenuItem
                  key={filter?.value}
                  className={`flex gap-3 text-xs outline-none hover:cursor-pointer hover:bg-gray-50`}
                  onSelect={() =>
                    setFilters((prev) => {
                      return { ...prev, sortOrder: filter };
                    })
                  }
                >
                  <div className="flex w-4 h-4 rounded-full border border-primary">
                    {isSelected ? (
                      <div className="w-2.5 h-2.5 bg-primary rounded-full m-auto" />
                    ) : null}
                  </div>
                  {filter?.label}
                </DropdownMenuItem>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default FilterSection;
